import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import cookies from 'js-cookie';
import moment from 'moment';
import {
  ChipVideoIcon,
  ChipImageIcon,
  PlaylistIcon,
  ChipUrlIcon,
  ChipPdfIcon,
} from '../shared/component/svgIcon';

export const getOicStorageKey = () => {
  const authSettings = cookies.get('secure-auth-access');
  if (authSettings) {
    return authSettings;
  }
  return null;
};

export const getI18Next = () => {
  const i18next = cookies.get('i18next');
  if (i18next) {
    return i18next;
  }
  return 'en';
};

export const getWejhaInfo = () => {
  const key = getOicStorageKey();
  if (key) {
    const oic = JSON.parse(key);
    return oic;
  }
  return null;
};

export const decodeJWTToken = (token) => {
  const payload = JSON.parse(atob(token.split('.')[1]));
  return payload;
};

export const getToken = () => {
  const wejha = getWejhaInfo();
  if (wejha) {
    return {
      Authorization: `Bearer ${wejha}`,
      // 'content-type': 'multipart/form-data',
    };
  }
  return null;
};

function formatDate(date, format) {
  let value = '';
  if (format === 'time') {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours %= 12;
    hours = hours || 12; // the hour '0' should be '12'
    hours = hours < 10 ? `0${hours.toString()}` : hours;
    minutes = minutes < 10 ? `0${minutes.toString()}` : minutes;
    const strTime = `${hours} : ${minutes} ${ampm}`;
    value = strTime;
  } else if (format === 'time_seconds') {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let seconds = date.getSeconds();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours %= 12;
    hours = hours || 12; // the hour '0' should be '12'
    hours = hours < 10 ? `0${hours.toString()}` : hours;
    minutes = minutes < 10 ? `0${minutes.toString()}` : minutes;
    seconds = seconds < 10 ? `0${seconds.toString()}` : seconds;
    const strTime = `${hours}:${minutes}:${seconds} ${ampm}`;
    value = strTime;
  } else {
    value = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  }
  return value;
}

export const changeDateFormat = (date) => {
  const time = formatDate(new Date(date), 'date');
  return time;
};

export const formatToDateType = (dateStr) => {
  const date = dateStr ? moment(dateStr).format('DD-MM-YYYY') : '';
  return date;
};

export const changeTimeFormat = (date) => {
  const time = formatDate(new Date(date), 'time');
  return time;
};

export const changeDateTimeFormat = (date) => {
  const time = `${changeDateFormat(date)} ${changeTimeFormat(date)}`;
  return time;
};
export const changeDateTimeSecondsFormat = (date) => {
  const time = `${changeDateFormat(date)} ${formatDate(new Date(date), 'time_seconds')}`;
  return time;
};
export const formatUTCToLocal = (utcDate, format = 'YYYY-MM-DD') => {
  if (!utcDate) return '';
  return moment.utc(utcDate).local().format(format);
};

export const formatFullDateTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const getTimeDuration = (startDate, endDate) => {
  const startTime = new Date(startDate).getTime();
  const endTime = new Date(endDate).getTime();
  const timeDifference = Math.abs(endTime - startTime);
  const hours = Math.floor(timeDifference / (1000 * 60 * 60));
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours} hrs ${minutes} mins`;
};

export const getDatesInRange = (startDate, endDate) => {
  const date = new Date(startDate.getTime());
  const dates = [];
  while (date <= endDate) {
    dates.push(new Date(date).toLocaleDateString());
    date.setDate(date.getDate() + 1);
  }
  return dates;
};
export function getCurrentLanguageCode() {
  return cookies.get('i18next') || 'en';
}

export function convertBytes(totalBytes) {
  let size;
  if (totalBytes < 1000000) {
    size = (`${(totalBytes / 1024).toFixed(2)} KB`);
  } else if (totalBytes < 1047511424) {
    size = (`${(totalBytes / 1024 / 1024).toFixed(2)} MB`);
  } else {
    size = (`${(totalBytes / 1024 / 1024 / 1024).toFixed(2)} GB`);
  }
  return size;
}
export function convertBytesToGB(totalBytes) {
  return (totalBytes / 1024 / 1024 / 1024).toFixed(2);
}

export function convertToMBorGB(value) {
  let finalValue;
  if (value >= 1) {
    finalValue = `${value?.toFixed(2)} GB`;
  } else {
    finalValue = `${(value * 1024)?.toFixed(2)} MB`;
  }
  return finalValue;
}

// Usage remains the same:
export function setLocalStorageItem(name, data) {
  if (name && data) {
    localStorage.setItem(name, JSON.stringify(data));
  }
}

export function setCookieStorageItem(name, data) {
  cookies.set(name, JSON.stringify(data));
}

export function getLocalStorageItem(name) {
  const data = JSON.parse(localStorage.getItem(name));
  return data;
}

export function getCookieStorageItem(name) {
  const cookieValue = cookies.get(name);
  if (cookieValue) {
    const data = JSON?.parse(cookieValue);
    return data;
  }
  return null;
}

export function removeLocalStorageItem(name) {
  localStorage.removeItem(name);
}

export function removeCookieStorageItem(name) {
  cookies.remove(name);
}

export function checkPermission(permission) {
  const data = JSON.parse(localStorage.getItem('role'));
  return data?.includes(permission);
}

// Check if the feature available or not based on feature_name and sbu_feature_name
export function checkFeatures(featureName, subFeatureName) {
  const featuresListString = localStorage.getItem('features');

  if (!featuresListString) {
    return null;
  }

  const featuresList = JSON.parse(featuresListString);

  const singleFeature = featuresList?.find((feature) => feature.name === featureName);

  if (singleFeature) {
    const singleSubFeature = singleFeature?.sub_features
      ?.find((subFeature) => subFeature.sub_feature_name === subFeatureName);

    if (singleSubFeature) {
      return singleSubFeature.value;
    }
  }
  return null;
}

export function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function addImageType(type) {
  const split = type?.split('/');
  let output;
  if (type === 'application/pdf') {
    output = 'PDF';
  } else if (type?.includes('template')) {
    output = 'Template';
  } else {
    output = split?.[0];
  }
  return <div className="icon-text">{output}</div>;
}
export function mediaIcon(type) {
  let output;
  if ((type?.includes('video') || type === 'youtube')) {
    output = <ChipVideoIcon />;
  } else if (type?.includes('image')) {
    output = <ChipImageIcon />;
  } else if (type === 'application/pdf') {
    output = <ChipPdfIcon />;
  } else if (type === 'playlist') {
    output = <PlaylistIcon />;
  } else if (type === 'url') {
    output = <ChipUrlIcon />;
  } else {
    output = <ChipImageIcon />;
  }
  return output;
}

export function statusFileType(type, isPosContent) {
  const split = type.split('/');
  let output;
  if (type === 'application/pdf') {
    output = 'PDF';
  } else if (isPosContent) {
    output = 'PosItEasy';
  } else {
    output = split?.[0];
  }
  return output;
}

export function timeFormat(data) {
  const hour = data?.$H;
  const minute = data?.$m;
  const formattedTime = `${hour < 10 ? '0' : ''}${hour}:${minute < 10 ? '0' : ''}${minute}`;
  return formattedTime;
}

export function useRecaptcha(action) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const VerifyRecaptcha = async () => {
    const recaptchaToken = await executeRecaptcha(action);
    return recaptchaToken;
  };
  return { VerifyRecaptcha };
}

export function dataURLtoFile(dataUrl, filename) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n > 0) {
    n -= 1;
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export function uploadImageResize(file) {
  const imageUrls = {};
  const img = document.createElement('img');
  img.onload = () => {
    const resizeImage = (targetWidth, targetHeight) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      return canvas.toDataURL('image/jpeg', 1.0); // Use quality of 1.0 for full quality
    };

    const width = img?.width;
    const height = img?.height;

    // 1. Resize to 8K
    if (width > 7680 && height > 4320) {
      imageUrls.image1 = resizeImage(7680, 4320);
      imageUrls.image2 = resizeImage(600, 400);
      imageUrls.image3 = resizeImage(50, 50);
    } else if ((width > 600 && width <= 7680) || (height > 400 && height <= 4320)) {
      imageUrls.image1 = resizeImage(width, height);
      imageUrls.image2 = resizeImage(600, 400);
      imageUrls.image3 = resizeImage(50, 50);
    } else if ((width > 25 && width <= 600) || (height > 25 && height <= 400)) {
      imageUrls.image1 = resizeImage(width, height);
      imageUrls.image2 = resizeImage(width, height);
      imageUrls.image3 = resizeImage(50, 50);
    } else {
      imageUrls.image1 = resizeImage(25, 25);
      imageUrls.image2 = resizeImage(25, 25);
      imageUrls.image3 = resizeImage(25, 25);
    }
  };
  img.src = file;
  return imageUrls;
}

export const getDataFormat = (filteredValues) => {
  const transformedData = filteredValues?.reduce((acc, { key, result }) => {
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(result);
    return acc;
  }, {});
  return transformedData;
};
